import { SprintProductStatus, useSprintProductsForViewer, ClientReportWithProductsEntity } from '@data';
import { useStore } from '@data/storages';
import React, { ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Loader } from '@ui';
import { ClipboardPaste } from 'lucide-react';
import { DateRangeFilter } from '@app/components/core';
import { EmptyState } from '@app/components/core/empty/EmptyState';
import ClientReportCard from './ClientReportCard';
import { isSprintVisited } from '../visitedSprints';
import { useIsInternalReader } from '../internalReader';

const sprintStart = (report: ClientReportWithProductsEntity): number | undefined =>
  report.sprint?.startDate ? new Date(report.sprint.startDate).getTime() : undefined;
const sprintEnd = (report: ClientReportWithProductsEntity): number | undefined =>
  report.sprint?.endDate ? new Date(report.sprint.endDate).getTime() : undefined;

/** `actions` share the date filter's row, lined up with its inputs. */
const SprintProducts = ({ actions }: { actions?: ReactNode }): JSX.Element => {
  const { organization } = useStore();
  const { productId } = useParams<{ productId: string }>();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const statuses = [SprintProductStatus.closed, SprintProductStatus.approved, SprintProductStatus.invoiced];

  const { data: sprintProducts, isLoading } = useSprintProductsForViewer(useIsInternalReader(), organization?.id, {
    statuses,
    productId,
  });

  // The endpoint is client-scoped, so it answers with every product the client owns. This tab lives on
  // one product's page: its reports, one per sprint, newest sprint first.
  const reports = useMemo<ClientReportWithProductsEntity[]>(
    () =>
      (sprintProducts?.items ?? [])
        .filter(item => item.product?.id === productId)
        // The product carries the report it stands for, because the row's Approve and Dispute read the
        // dispute state per product rather than off the card. One product per card here, so it is this item.
        .map(item => ({ ...item, products: [{ ...item.product, report: item }] }))
        // By start date, not by sprint number: numbering restarts at 1 every year, so ordering on it
        // interleaves the years once a client has more than one.
        .sort(
          (a, b) => (sprintStart(b) ?? 0) - (sprintStart(a) ?? 0) || (b.sprint?.number ?? 0) - (a.sprint?.number ?? 0),
        ),
    [sprintProducts, productId],
  );

  const { t } = useTranslation();

  const now = Date.now();
  const fromTime = from ? new Date(from).getTime() : undefined;
  const toTime = to ? new Date(`${to}T23:59:59.999`).getTime() : undefined;

  // The filter only applies once BOTH dates are set; if only one is, flag the empty one as required.
  const rangeActive = Boolean(from && to);
  const fromMissing = Boolean(to) && !from;
  const toMissing = Boolean(from) && !to;

  // Keep sprints that overlap the selected [from, to] window (either bound optional).
  const inRange = (report: ClientReportWithProductsEntity): boolean => {
    const start = sprintStart(report);
    const end = sprintEnd(report);
    if (fromTime !== undefined && end !== undefined && end < fromTime) return false;
    if (toTime !== undefined && start !== undefined && start > toTime) return false;
    return true;
  };
  const isCurrentSprint = (report: ClientReportWithProductsEntity): boolean => {
    const start = sprintStart(report);
    const end = sprintEnd(report);
    return start !== undefined && end !== undefined && start <= now && now <= end;
  };

  const filtered = rangeActive ? reports.filter(inRange) : reports;
  const currentReports = filtered.filter(isCurrentSprint);
  const pastReports = filtered.filter(report => !isCurrentSprint(report));

  const renderCard = (report: ClientReportWithProductsEntity) => (
    <ClientReportCard
      key={report.id}
      report={report}
      organizationId={organization?.id}
      showNewBadge={!isSprintVisited(productId, report.sprint?.id)}
      // A report the client has already answered is not waiting on them, so the row stops offering the
      // choice — the same rule the report page itself follows.
      isApprovable={report.status === SprintProductStatus.closed}
    />
  );

  const toolbar = (withFilter: boolean) => (
    // One row on a phone: the dates share what the (icon-only) action leaves them.
    <div className="mb-6 flex items-end justify-between gap-3 sm:flex-wrap">
      {withFilter && (
        <DateRangeFilter
          className="min-w-0 flex-1 sm:flex-none"
          from={from}
          to={to}
          onFromChange={setFrom}
          onToChange={setTo}
          fromMissing={fromMissing}
          toMissing={toMissing}
        />
      )}
      {actions && <div className="ml-auto shrink-0 print:hidden">{actions}</div>}
    </div>
  );

  let content: JSX.Element;
  if (isLoading) {
    content = <Loader />;
  } else if (!reports.length) {
    content = (
      <>
        {actions && toolbar(false)}
        <EmptyState icon={ClipboardPaste} title={t('noReportsYet')} subtitle={t('sendOutMsgAfterSprintEnd')} />
      </>
    );
  } else {
    content = (
      <>
        {toolbar(true)}
        {!filtered.length ? (
          <p className="text-sm text-muted-foreground">{t('noEntriesFound')}</p>
        ) : (
          <div className="flex flex-col gap-8">
            {currentReports.length > 0 && (
              <section>
                <h2 className="mb-2 font-bold text-foreground">{t('tabs.currentSprint')}</h2>
                {currentReports.map(renderCard)}
              </section>
            )}
            {pastReports.length > 0 && (
              <section>
                {currentReports.length > 0 && (
                  <h2 className="mb-2 font-bold text-foreground">{t('tabs.pastSprints')}</h2>
                )}
                {pastReports.map(renderCard)}
              </section>
            )}
          </div>
        )}
      </>
    );
  }

  return content;
};

export default SprintProducts;
